import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { api } from '@/lib/api';
import { identifyUser, resetUser } from '@/lib/analytics';
import type { User, RegisterData } from '@/types';

// Re-export User type for convenience
export type { User };

// =============================================================================
// Ghost Session Helpers
// =============================================================================

const GHOST_TOKEN_KEY = 'ghost_token';
const GHOST_USER_KEY = 'ghost_user';
const GHOST_IMPERSONATOR_KEY = 'ghost_impersonated_by';

/** Check if the current tab is a ghost (impersonation) session */
export function isGhostSession(): boolean {
  return Boolean(sessionStorage.getItem(GHOST_TOKEN_KEY));
}

/** Get ghost session info (returns null if not a ghost session) */
export function getGhostInfo(): { impersonatedBy: string; userName: string } | null {
  if (!isGhostSession()) return null;
  const impersonatedBy = sessionStorage.getItem(GHOST_IMPERSONATOR_KEY) || 'Admin';
  const userJson = sessionStorage.getItem(GHOST_USER_KEY);
  const user = userJson ? JSON.parse(userJson) : null;
  const userName = user ? `${user.firstName} ${user.lastName}` : 'Unknown';
  return { impersonatedBy, userName };
}

/** Clear ghost session data */
export function clearGhostSession(): void {
  sessionStorage.removeItem(GHOST_TOKEN_KEY);
  sessionStorage.removeItem(GHOST_USER_KEY);
  sessionStorage.removeItem(GHOST_IMPERSONATOR_KEY);
  sessionStorage.removeItem('auth-storage');
}

/**
 * Ghost-aware storage adapter for Zustand persist.
 *
 * - Ghost tab (sessionStorage has ghost_token): reads/writes sessionStorage
 *   so it NEVER overwrites the admin's real session in localStorage.
 * - Normal tab: reads/writes localStorage as usual.
 */
const ghostAwareStorage = {
  getItem: (name: string): string | null => {
    if (isGhostSession()) {
      return sessionStorage.getItem(name);
    }
    return localStorage.getItem(name);
  },
  setItem: (name: string, value: string): void => {
    if (isGhostSession()) {
      sessionStorage.setItem(name, value);
      return;
    }
    localStorage.setItem(name, value);
  },
  removeItem: (name: string): void => {
    if (isGhostSession()) {
      sessionStorage.removeItem(name);
      return;
    }
    localStorage.removeItem(name);
  },
};

// =============================================================================
// Auth Store
// =============================================================================

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  _hasHydrated: boolean;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  clearAuth: () => void;
  refreshTokens: () => Promise<void>;
  refreshUser: () => Promise<void>;
  setLoading: (loading: boolean) => void;
  checkAuth: () => Promise<void>;
  setHasHydrated: (state: boolean) => void;
  setTokens: (accessToken: string, refreshToken: string | null) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,
      _hasHydrated: false,

      setHasHydrated: (state: boolean) => {
        set({ _hasHydrated: state });
      },

      setTokens: (accessToken: string, refreshToken: string | null) => {
        set({
          accessToken,
          refreshToken,
          user: null, // Clear stale user so checkAuth() fetches fresh data from API
          isAuthenticated: true,
          isLoading: false,
        });
      },

      login: async (email: string, password: string) => {
        const response = await api.post('/auth/login', { email, password });
        const { accessToken, refreshToken, user } = response.data.data;

        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
        });

        // Link analytics session to the user id. `login` itself is written
        // server-side (one writer per fact).
        identifyUser(user);
      },

      register: async (data) => {
        const response = await api.post('/auth/register', data);
        const { accessToken, refreshToken, user } = response.data.data;

        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
        });

        identifyUser(user); // `sign_up` is written server-side
      },

      logout: async () => {
        // Ghost sessions: just clear sessionStorage and done
        if (isGhostSession()) {
          clearGhostSession();
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
          });
          return;
        }

        resetUser();

        try {
          await api.post('/auth/logout');
        } catch {
          // Ignore errors on logout
        }

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      clearAuth: () => {
        // Ghost sessions: clear sessionStorage
        if (isGhostSession()) {
          clearGhostSession();
        }

        // Clear auth state without making API call (used when token refresh fails)
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      refreshTokens: async () => {
        // Ghost sessions have no refresh token — they just expire
        if (isGhostSession()) {
          throw new Error('Ghost sessions cannot be refreshed');
        }

        const { refreshToken } = get();
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        const response = await api.post('/auth/refresh', { refreshToken });
        const { accessToken, refreshToken: newRefreshToken } = response.data.data;

        set({
          accessToken,
          refreshToken: newRefreshToken,
        });
      },

      refreshUser: async () => {
        const { accessToken } = get();
        if (!accessToken) return;

        const response = await api.get('/auth/me');
        const user = response.data.data;
        set({ user });
        if (!isGhostSession()) {
          identifyUser(user);
        }
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      checkAuth: async () => {
        const { accessToken, refreshToken, user } = get();

        // If we have user and token from persisted state, mark as authenticated
        if (accessToken && user) {
          set({ isAuthenticated: true, isLoading: false });
          // Re-identify user for analytics on session restore (skip for ghost)
          if (!isGhostSession()) {
            identifyUser(user);
          }
          // Self-heal the persisted user in the background: sessions persisted
          // before /login returned teamRole+capabilities (or after a role/team
          // change) would otherwise keep a stale user object forever, hiding
          // capability-gated UI. Non-blocking; failures are harmless (the 401
          // interceptor handles expired tokens).
          void get()
            .refreshUser()
            .catch(() => {});
          return;
        }

        if (!accessToken && !refreshToken) {
          set({ isLoading: false, isAuthenticated: false });
          return;
        }

        try {
          const response = await api.get('/auth/me');
          set({
            user: response.data.data,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch {
          // Ghost sessions: no refresh, just fail
          if (isGhostSession()) {
            clearGhostSession();
            set({
              user: null,
              accessToken: null,
              refreshToken: null,
              isAuthenticated: false,
              isLoading: false,
            });
            return;
          }

          // Try to refresh token
          if (refreshToken) {
            try {
              await get().refreshTokens();
              const response = await api.get('/auth/me');
              set({
                user: response.data.data,
                isAuthenticated: true,
                isLoading: false,
              });
            } catch {
              set({
                user: null,
                accessToken: null,
                refreshToken: null,
                isAuthenticated: false,
                isLoading: false,
              });
            }
          } else {
            set({
              user: null,
              accessToken: null,
              refreshToken: null,
              isAuthenticated: false,
              isLoading: false,
            });
          }
        }
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => ghostAwareStorage),
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Called after hydration is complete
        if (state) {
          state.setHasHydrated(true);
          // If we have tokens from storage, validate them
          if (state.accessToken || state.refreshToken) {
            state.checkAuth();
          } else {
            state.setLoading(false);
          }
        }
      },
    }
  )
);

// Don't call checkAuth immediately - let onRehydrateStorage handle it

// =============================================================================
// Token Resolution
// =============================================================================

/**
 * The single answer to "which bearer token is this tab acting as?".
 *
 * Two stores can hold a token: a ghost (impersonation) tab keeps its own in
 * sessionStorage and must never act as the real admin, while a normal session
 * lives in this store (persisted as the `auth-storage` JSON blob, so the raw
 * token is NOT readable under a top-level storage key). Every consumer has to
 * agree, which is why resolution lives here instead of being re-derived at each
 * call site: useChatbotSocket used to read a `localStorage.accessToken` key
 * that nothing in the app writes, so admin chat realtime never authenticated.
 */
export function getAuthToken(): string | null {
  const ghostToken = sessionStorage.getItem(GHOST_TOKEN_KEY);
  if (ghostToken) return ghostToken;
  return useAuthStore.getState().accessToken;
}
